import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Briefcase, Globe2, BookOpen, Building2, FileText, ArrowUpRight, Check } from "lucide-react";

const ICONS = {
  personal: FileText,
  business: Briefcase,
  expats: Globe2,
  bookkeeping: BookOpen,
  formation: Building2,
};

const IMAGES = {
  personal:
    "https://images.unsplash.com/photo-1758687126877-b37052a20a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBob3VzZSUyMGhhcHB5fGVufDB8fHx8MTc4MTQxMDIwMXww&ixlib=rb-4.1.0&q=85",
  business:
    "https://images.unsplash.com/photo-1753351052277-a7ff634f0bd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBjYWZlJTIwaGFwcHl8ZW58MHx8fHwxNzgxNDEwMTkyfDA&ixlib=rb-4.1.0&q=85",
  expats:
    "https://images.unsplash.com/photo-1581553673739-c4906b5d0de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwYXNzcG9ydCUyMHRyYXZlbCUyMGV4cGF0JTIwZmluYW5jZXxlbnwwfHx8fDE3ODE0MTAxOTF8MA&ixlib=rb-4.1.0&q=85",
  bookkeeping:
    "https://images.unsplash.com/photo-1707157284454-553ef0a4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBhY2NvdW50YW50JTIwb2ZmaWNlfGVufDB8fHx8MTc4MTQxMDIwMXww&ixlib=rb-4.1.0&q=85",
  formation:
    "https://images.unsplash.com/photo-1753351052277-a7ff634f0bd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBjYWZlJTIwaGFwcHl8ZW58MHx8fHwxNzgxNDEwMTkyfDA&ixlib=rb-4.1.0&q=85",
};

export default function ServicesGrid({ withImages = false, compact = false }) {
  const { t } = useLanguage();
  const items = t.services.items;

  return (
    <section
      id="services"
      className={`${compact ? "py-16" : "py-24"} bg-brand-bg`}
      data-testid="services-grid"
    >
      <div className="container-pad">
        <div className="max-w-2xl">
          <div className="overline">{t.services.overline}</div>
          <h2 className="mt-3 text-4xl sm:text-5xl leading-[1.05]">{t.services.title}</h2>
          <p className="mt-5 text-brand-text/70 text-lg leading-relaxed">{t.services.subtitle}</p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((s, i) => {
            const Icon = ICONS[s.key] || FileText;
            const isFeatured = i === 1;
            return (
              <div
                key={s.key}
                id={s.key}
                data-testid={`service-card-${s.key}`}
                className={`group relative flex flex-col rounded-xl border border-brand-line bg-brand-surface p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  isFeatured ? "lg:row-span-1" : ""
                }`}
              >
                {withImages && (
                  <div className="mb-6 -mx-7 -mt-7 h-44 overflow-hidden rounded-t-xl">
                    <img
                      src={IMAGES[s.key]}
                      alt={s.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-subtle text-brand-primary">
                  <Icon size={22} />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-brand-primary">{s.name}</h3>
                <p className="mt-3 text-brand-text/70 leading-relaxed text-[15px]">{s.desc}</p>

                <ul className="mt-5 space-y-2 text-sm text-brand-text/75">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className="mt-0.5 text-brand-accent shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`service-cta-${s.key}`}
                  className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary group-hover:text-brand-accent transition-colors"
                >
                  {t.services.learn_more}
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
